import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Input,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@patient-watch/ui";
import { AdminPasswordGate } from "../components/AdminPasswordGate";
import { AdminLayout } from "../components/AdminLayout";
import { useAdminDashboard, type AdminProfileSummary } from "../hooks/useAdminDashboard";
import { useAssignStudyNumber } from "../hooks/useAssignStudyNumber";
import { formatStudyNumber } from "../utils/studyTrackerHelpers";

export const Route = createFileRoute("/admin")({
    component: AdminComponent,
});

function AdminComponent() {
    return (
        <AdminPasswordGate description="Enter the study admin password to view participant data.">
            <AdminDashboard />
        </AdminPasswordGate>
    );
}

function maskPhone(phone: string) {
    if (phone.length < 4) return phone;
    return phone.slice(0, -4).replace(/\d/g, "*") + phone.slice(-4);
}

function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

function completionRate(profile: AdminProfileSummary) {
    return profile.total_entries > 0 ? Math.round((profile.submitted_count / profile.total_entries) * 100) : 0;
}

function PendingParticipantRow({ profile }: { profile: AdminProfileSummary }) {
    const assignStudyNumber = useAssignStudyNumber();
    const [studyNumberInput, setStudyNumberInput] = useState("");

    const handleAssign = () => {
        const studyNumber = Number(studyNumberInput);
        if (!Number.isInteger(studyNumber) || studyNumber <= 0) {
            toast.error("Enter a valid study number.");
            return;
        }

        assignStudyNumber.mutate(
            { profileId: profile.profile_id, studyNumber },
            {
                onSuccess: (updatedProfile) => {
                    toast.success(`Assigned ${formatStudyNumber(updatedProfile.study_number)}.`);
                    setStudyNumberInput("");
                },
                onError: (error) => {
                    toast.error(error instanceof Error ? error.message : "Failed to assign study number.");
                },
            }
        );
    };

    return (
        <TableRow className="bg-amber-50/60">
            <TableCell>
                <Badge variant="outline" className="border-amber-300 text-amber-800">
                    Not enrolled
                </Badge>
            </TableCell>
            <TableCell>{maskPhone(profile.phone)}</TableCell>
            <TableCell>{formatDate(profile.created_at)}</TableCell>
            <TableCell className="font-medium">{profile.submitted_count}</TableCell>
            <TableCell>{profile.total_entries}</TableCell>
            <TableCell>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        min={1}
                        step={1}
                        placeholder="e.g. 7"
                        value={studyNumberInput}
                        onChange={(event) => setStudyNumberInput(event.target.value)}
                        className="w-24 h-9 bg-white"
                    />
                    <Button
                        size="sm"
                        disabled={assignStudyNumber.isPending || studyNumberInput.trim() === ""}
                        onClick={handleAssign}
                    >
                        {assignStudyNumber.isPending ? (
                            <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Saving
                            </>
                        ) : (
                            "Assign"
                        )}
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

function AdminDashboard() {
    const { data, isLoading, isError, error } = useAdminDashboard(true);
    const enrolledProfiles = data?.enrolledProfiles ?? [];
    const pendingProfiles = data?.pendingProfiles ?? [];

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="py-12 text-center">Loading admin dashboard...</div>
            </AdminLayout>
        );
    }

    if (isError || !data) {
        return (
            <AdminLayout>
                <Card className="mx-auto max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <h2 className="mb-4 font-bold text-red-600 text-2xl">Error</h2>
                            <p className="text-gray-600">
                                {error instanceof Error ? error.message : "Failed to load admin dashboard."}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </AdminLayout>
        );
    }

    const totalSubmissions = enrolledProfiles.reduce((sum, p) => sum + p.submitted_count, 0);
    const totalEntries = enrolledProfiles.reduce((sum, p) => sum + p.total_entries, 0);
    const completionRateValue = totalEntries > 0 ? Math.round((totalSubmissions / totalEntries) * 100) : 0;

    return (
        <AdminLayout>
            <div className="gap-4 grid grid-cols-1 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-medium text-sm">Enrolled Participants</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-bold text-2xl">{enrolledProfiles.length}</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-medium text-sm">Total Submissions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-bold text-2xl">{totalSubmissions}</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-medium text-sm">Total Forms Sent</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-bold text-2xl">{totalEntries}</div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-medium text-sm">Completion Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-bold text-2xl">{completionRateValue}%</div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Enrolled Participants</CardTitle>
                    <CardDescription>Included in study tracker, exports, and completion statistics</CardDescription>
                </CardHeader>
                <CardContent>
                    {enrolledProfiles.length === 0 ? (
                        <p className="text-slate-600 text-sm">No enrolled participants yet.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Study Number</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Joined</TableHead>
                                    <TableHead>Forms Completed</TableHead>
                                    <TableHead>Total Forms</TableHead>
                                    <TableHead>Completion Rate</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {enrolledProfiles.map((profile) => {
                                    const rate = completionRate(profile);

                                    return (
                                        <TableRow key={profile.profile_id}>
                                            <TableCell className="font-medium">
                                                {formatStudyNumber(profile.study_number!)}
                                            </TableCell>
                                            <TableCell>{maskPhone(profile.phone)}</TableCell>
                                            <TableCell>{formatDate(profile.created_at)}</TableCell>
                                            <TableCell className="font-medium">{profile.submitted_count}</TableCell>
                                            <TableCell>{profile.total_entries}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        rate >= 80
                                                            ? "default"
                                                            : rate >= 50
                                                              ? "secondary"
                                                              : "destructive"
                                                    }
                                                >
                                                    {rate}%
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={profile.submitted_count > 0 ? "default" : "outline"}>
                                                    {profile.submitted_count > 0 ? "Active" : "Inactive"}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Card className="border-amber-200">
                <CardHeader>
                    <CardTitle className="text-amber-950">Awaiting Study Number</CardTitle>
                    <CardDescription className="text-amber-900/80">
                        These participants are registered but not enrolled yet. They are excluded from the study
                        tracker, exports, and the enrolled totals above until you assign a study number.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {pendingProfiles.length === 0 ? (
                        <p className="text-slate-600 text-sm">Everyone registered has a study number assigned.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Joined</TableHead>
                                    <TableHead>Forms Completed</TableHead>
                                    <TableHead>Total Forms</TableHead>
                                    <TableHead>Assign Study Number</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendingProfiles.map((profile) => (
                                    <PendingParticipantRow key={profile.profile_id} profile={profile} />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </AdminLayout>
    );
}
